import { useMemo, useContext } from 'react'
import { styled } from '@mui/material/styles'
import Modal from '@mui/material/Modal'
import Divider from '@mui/material/Divider'
import Stack from '@mui/material/Stack'
import Button from '@mui/material/Button'
import { useParams, useSearchParams, useNavigate, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useFormContext } from 'react-hook-form'

import { ModifyEventSchema } from '../../../../events/Events.schema'
import { SubEventsStep2Context } from '../SubEventsStep2.Context'
import SubEventForm from './SubEventForm'

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  bgcolor: 'background.paper',
  borderRadius: { xs: 0, sm: 6 },
  boxShadow: 24,
  padding: { xs: 1, sm: 3 },
  width: { xs: '100%', sm: '90%' },
  maxWidth: '70rem',
  height: { xs: '100%', sm: '80%' },
  overflow: 'auto',
}

const Title = styled('h2')(() => ({
  marginBottom: 0,
}))

export default function ModifySubEvent() {
  const { t } = useTranslation('events')
  const navigate = useNavigate()
  const location = useLocation()
  const { eventId, subEventId } = useParams()
  const [searchParams] = useSearchParams()
  if (!eventId || !subEventId) {
    throw new Error('eventId is required')
  }

  const { eventStart, eventEnd, eventLocation, removeSubEvent, updateSession } =
    useContext(SubEventsStep2Context)
  const methods = useFormContext<ModifyEventSchema>()
  const { watch, getValues, setValue } = methods

  const subEvents = watch('subEvents')
  const [subEvent, subEventKey] = useMemo(() => {
    let index = -1
    const res = subEvents.find((subEvent, key) => {
      if (Number(subEvent.id) === Number(subEventId)) {
        index = key
        return true
      }
      return false
    })
    return [res, index]
  }, [subEvents, subEventId])

  const handleClose = () => {
    searchParams.set('subEventId', subEventId)
    const parentUrl = location.pathname.replace(
      /\/subEvent\/(\w|\d)+/,
      `?${searchParams.toString()}`
    )
    navigate(parentUrl)
  }

  return (
    <Modal open onClose={handleClose}>
      <Stack sx={style} gap="0.5rem">
        <Stack>
          <Stack direction="row" justifyContent="space-between">
            <Title>{t("Détails de l'intervention")}</Title>
          </Stack>
          <Divider />
        </Stack>
        <SubEventForm
          key={subEvent?.id ?? -1}
          id={subEvent?.id ?? -1}
          subEventKey={subEventKey}
          minDateTime={eventStart ? new Date(eventStart) : undefined}
          maxDateTime={eventEnd ? new Date(eventEnd) : undefined}
          location={eventLocation}
          onUpdateSession={(reason, details) => {
            if (reason === 'selectOption' && details?.option) {
              const sessions = getValues('sessions')
              const sessionKey = sessions.findIndex(session => session.id === details.option.id)
              const ids = (getValues(`sessions.${sessionKey}.sub_events`) ?? []).concat(
                details.option.id
              )
              setValue(`sessions.${sessionKey}.sub_events`, ids)
              updateSession(sessionKey, {
                ...sessions[sessionKey],
                sub_events: ids,
              })
            }
            if (reason === 'removeOption' && details?.option) {
              const sessions = getValues('sessions')
              const sessionKey = sessions.findIndex(session => session.id === details.option.id)
              const ids = (getValues(`sessions.${sessionKey}.sub_events`) ?? []).filter(
                id => id !== details.option.id
              )
              setValue(`sessions.${sessionKey}.sub_events`, ids)
              updateSession(sessionKey, {
                ...sessions[sessionKey],
                sub_events: ids,
              })
            }
          }}
          onRemove={() => {
            if (window.confirm(t('Êtes-vous sûr de vouloir supprimer cette intervention ?'))) {
              removeSubEvent(subEventKey)
              handleClose()
            }
          }}
        />
        <Stack
          direction="row"
          justifyContent={'space-between'}
          paddingBottom="1.5rem"
          sx={{
            position: 'sticky',
            bottom: '-1rem',
            zIndex: 1,
            pointerEvents: 'none',
          }}
        >
          <Button variant="contained" sx={{ pointerEvents: 'all' }} onClick={handleClose}>
            {t('Fermer')}
          </Button>
        </Stack>
      </Stack>
    </Modal>
  )
}
